import React, { useState, useRef, useEffect } from 'react';
import type { TreeCarouselProps } from '../../types';
import TreeCard from './TreeCard';
import styles from './TreeCarousel.module.css';

const TreeCarousel: React.FC<TreeCarouselProps> = ({
  treeSpecies,
  onTreeSelect,
  onTreeDrag
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Create infinite loop by duplicating species array
  const getInfiniteSpecies = () => {
    if (treeSpecies.length === 0) return [];
    const duplicateCount = Math.ceil(15 / treeSpecies.length); // Ensure we have enough for smooth infinite scroll
    const duplicatedSpecies = [];
    for (let i = 0; i < duplicateCount; i++) {
      duplicatedSpecies.push(...treeSpecies.map(species => ({ ...species, id: `${species.id}-${i}` })));
    }
    return duplicatedSpecies;
  };

  const infiniteSpecies = getInfiniteSpecies();
  const visibleCards = 5; // Show 5 cards for coverflow effect
  const centerIndex = Math.floor(visibleCards / 2);

  // Initialize to center of the infinite array
  useEffect(() => {
    if (infiniteSpecies.length > 0) {
      const centerStart = Math.floor(infiniteSpecies.length / 2) - centerIndex;
      setCurrentIndex(centerStart);
    }
  }, [infiniteSpecies.length, centerIndex]);

  const scrollNext = () => {
    if (isTransitioning || infiniteSpecies.length === 0) return;
    
    setIsTransitioning(true);
    setCurrentIndex(prev => {
      const newIndex = prev + 1;
      // Reset to beginning of cycle when we've gone through all original species
      if (newIndex >= infiniteSpecies.length - centerIndex) {
        setTimeout(() => {
          setCurrentIndex(centerIndex);
          setIsTransitioning(false);
        }, 300);
        return newIndex;
      }
      return newIndex;
    });
    
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const scrollPrev = () => {
    if (isTransitioning || infiniteSpecies.length === 0) return;
    
    setIsTransitioning(true);
    setCurrentIndex(prev => {
      const newIndex = prev - 1;
      // Reset to end of cycle when we've gone before the beginning
      if (newIndex < centerIndex) {
        setTimeout(() => {
          setCurrentIndex(infiniteSpecies.length - centerIndex - 1);
          setIsTransitioning(false);
        }, 300);
        return newIndex;
      }
      return newIndex;
    });
    
    setTimeout(() => setIsTransitioning(false), 300);
  };

  // Calculate visible range
  const getVisibleRange = () => {
    const start = Math.max(0, currentIndex - centerIndex);
    const end = Math.min(infiniteSpecies.length, start + visibleCards);
    return { start, end };
  };

  const { start, end } = getVisibleRange();
  const visibleSpecies = infiniteSpecies.slice(start, end);

  // Calculate transform and scale for each card in coverflow effect
  const getCardTransform = (index: number) => {
    const relativeIndex = index - centerIndex;
    const rotationY = relativeIndex * 20; // Reduce rotation for better visibility
    const translateX = relativeIndex * 310; // Increase horizontal spacing to prevent overlap
    const translateZ = Math.abs(relativeIndex) * -450; // Reduce depth for better visibility
    const scale = relativeIndex === 0 ? 1 : 0.9; // Make side cards slightly bigger
    
    return {
      transform: `translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotationY}deg) scale(${scale})`,
      zIndex: 10 - Math.abs(relativeIndex), // Ensure center card is always on top
      opacity: relativeIndex === 0 ? 1 : 0.7
    };
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      scrollNext();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      scrollPrev();
    }
  };

  const handleCardClick = (index: number) => {
    const relativeIndex = index - centerIndex;
    if (relativeIndex > 0) {
      // Click on right side card - scroll to it
      scrollNext();
    } else if (relativeIndex < 0) {
      // Click on left side card - scroll to it
      scrollPrev();
    }
    // Center card is already selected, no action needed
  };

  return (
    <div 
      ref={containerRef}
      className={styles.carouselContainer}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="region"
      aria-label="Tree species carousel"
    >
      <div className={styles.carouselHeader}>
        <div className={styles.carouselTitle}>Climate-Adapted Tree Species</div>
        <div className={styles.carouselSubtitle}>
          Drag and drop trees onto the map to propose planting locations
        </div>
      </div>
      
      <div className={styles.carouselWrapper}>
        <div
          ref={carouselRef}
          className={styles.treeCarousel}
        >
          {visibleSpecies.map((tree, index) => {
            const cardStyle = getCardTransform(index);
            const isSelected = index === centerIndex;
            return (
              <div
                key={tree.id}
                className={styles.cardWrapper}
                style={{
                  ...cardStyle,
                  transition: isTransitioning ? 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
                }}
                onClick={() => handleCardClick(index)}
              >
                <TreeCard
                  tree={tree}
                  onDrag={onTreeDrag}
                  onSelect={onTreeSelect}
                  isSelected={isSelected}
                />
              </div>
            );
          })}
        </div>
        
        {/* Navigation Arrows */}
        <button
          className={`${styles.carouselNav} ${styles.prev}`}
          onClick={scrollPrev}
          disabled={isTransitioning}
          aria-label="Previous trees"
        >
          ‹
        </button>
        
        <button
          className={`${styles.carouselNav} ${styles.next}`}
          onClick={scrollNext}
          disabled={isTransitioning}
          aria-label="Next trees"
        >
          ›
        </button>
      </div>
    </div>
  );
};

export default TreeCarousel;